import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { authFetch, getLocalApiUrl } from '../../config/api'
import { formatSydneyDateTime } from '../../utils/sydneyDateTime'
import {
  UserIcon,
  BuildingOffice2Icon,
  XCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import GreenIdScreeningSummary from './GreenIdScreeningSummary'
import {
  getMatchDetailTitle,
  getMatchCardSubtitle,
  isWatchlistAmlScreening
} from '../../utils/amlMatchPresentation'
import { sanitizeVendorBranding } from '../../utils/sanitizeVendorBranding'
import { formatThreatLevel } from '../../utils/amlResultCategories'
import AmlMediaSource from './AmlMediaSource'

/**
 * Provider-aware AML match detail modal (watchlist hits vs reference-database person entity).
 */
export default function AmlMatchDetailModal({
  match,
  screeningSummary = null,
  provider = null,
  onClose,
  getRiskBadgeColor,
  getRiskLevelColor,
  getConfidenceColor,
  getThreatLevelColor
}) {
  const navigate = useNavigate()
  const [caseForm, setCaseForm] = useState({ open: false, memberId: '', saving: false, error: '' })

  if (!match) return null

  const hitName = getMatchDetailTitle(match)
  const safeName = String(hitName || 'aml-hit').replace(/[^A-Za-z0-9_-]+/g, '_').slice(0, 60)

  const exportData = () => {
    const blob = new Blob(
      [JSON.stringify({ exportedAt: new Date().toISOString(), provider, screeningSummary, match }, null, 2)],
      { type: 'application/json' }
    )
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `AML_hit_${safeName}.json`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  const generateReport = () => {
    const esc = (v) => String(v ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
    const list = (arr) => (Array.isArray(arr) && arr.length ? arr.map(esc).join(', ') : '')
    const rows = [
      ['Name', hitName],
      ['Hit type', match.detailKindLabel || match.matchKind],
      ['Risk level', match.riskLevel],
      ['Risk category', match.riskCategory],
      ['Watchlist / source', match.riskDescription],
      ['Confidence', match.confidence != null ? `${match.confidence}%` : ''],
      ['Gender', match.gender],
      ['Nationality', match.nationality],
      ['Countries', list(match.countries)],
      ['Aliases', list(match.aliases)],
      ['Roles', list(match.roles)],
      ['Sanctions', list(match.sanctions)],
      ['Last updated', match.lastUpdated],
      ['Notes', match.profileNotes],
    ].filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== '')
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>AML screening hit - ${esc(hitName)}</title>
<style>body{font-family:system-ui,sans-serif;margin:32px;color:#111}h1{font-size:20px}table{border-collapse:collapse;width:100%}
td{border-bottom:1px solid #ddd;padding:8px;vertical-align:top;font-size:13px}td:first-child{width:200px;color:#555}small{color:#666}</style></head>
<body><h1>AML / PEP screening hit</h1><table>${rows.map(([k, v]) => `<tr><td>${esc(k)}</td><td>${k === 'Countries' || k === 'Aliases' || k === 'Roles' || k === 'Sanctions' ? v : esc(v)}</td></tr>`).join('')}</table>
<p><small>Generated ${esc(formatSydneyDateTime(new Date()))} from PaySafeGo AML Screening.</small></p></body></html>`
    const w = window.open('', '_blank')
    if (!w) return
    w.document.write(html)
    w.document.close()
    w.focus()
    setTimeout(() => w.print(), 250)
  }

  const submitCase = async () => {
    const memberId = caseForm.memberId.trim()
    if (!memberId) return setCaseForm((f) => ({ ...f, error: 'Enter the patron member ID to open the case against.' }))
    setCaseForm((f) => ({ ...f, saving: true, error: '' }))
    try {
      const reason = `AML/PEP screening hit: ${hitName}${match.riskCategory ? ` (${match.riskCategory})` : ''}${match.riskDescription ? ` - ${match.riskDescription}` : ''}`.slice(0, 2000)
      const res = await authFetch(getLocalApiUrl('/api/cases/manual'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ member_id: memberId, reason }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || data?.success === false) {
        throw new Error(data?.error || data?.message || `Could not create case (HTTP ${res.status})`)
      }
      const created = data.case || {}
      onClose?.()
      if (created.id) navigate(`/compliance-safe/investigations/${encodeURIComponent(created.id)}`)
    } catch (e) {
      setCaseForm((f) => ({ ...f, saving: false, error: e?.message || 'Could not create case' }))
    }
  }

  const watchlistScreening = isWatchlistAmlScreening(match, provider)
  const detailTitle = getMatchDetailTitle(match)
  const subtitle = getMatchCardSubtitle(match)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[200]">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              {match.matchKind === 'person' || match.gender ? (
                <UserIcon className="h-6 w-6 mr-2 shrink-0" />
              ) : (
                <BuildingOffice2Icon className="h-6 w-6 mr-2 shrink-0" />
              )}
              <span>{detailTitle}</span>
            </h2>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XCircleIcon className="h-6 w-6" />
            </button>
          </div>

          {watchlistScreening && screeningSummary && (
            <GreenIdScreeningSummary summary={screeningSummary} compact />
          )}

          {watchlistScreening && match.detailKindLabel && (
            <p className="text-sm text-slate-600 mb-4">
              Hit type: <strong>{match.detailKindLabel}</strong>
              {match.matchKind === 'pep_group' && match.sourceListName && (
                <span className="block mt-1 text-gray-600">
                  List: {match.sourceListName}
                </span>
              )}
            </p>
          )}

          {subtitle && watchlistScreening && (
            <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-gray-800">
              {match.matchKind === 'media_article' ? (
                <>
                  <span className="font-medium text-gray-700">Headline</span>
                  <p className="mt-1">{match.displayTitle || subtitle}</p>
                </>
              ) : (
                subtitle
              )}
            </div>
          )}

          {match.riskLevel && (
            <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
                Risk assessment
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <div
                    className={`inline-block px-3 py-2 text-sm font-bold rounded ${getRiskLevelColor(match.riskLevel)}`}
                  >
                    {match.riskLevel}
                    {!String(match.riskLevel).toLowerCase().includes('risk') ? ' Risk' : ''}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">Overall risk level</div>
                </div>
                {match.threatLevel && (
                  <div className="text-center">
                    <div
                      className={`inline-block px-3 py-2 text-sm font-medium rounded border ${getThreatLevelColor(formatThreatLevel(match.threatLevel))}`}
                    >
                      {formatThreatLevel(match.threatLevel)}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">Watchlist status</div>
                  </div>
                )}
                <div className="text-center">
                  <div
                    className={`inline-block px-3 py-2 text-sm font-medium rounded ${getRiskBadgeColor(match.riskCategory)}`}
                  >
                    {match.riskCategory}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">Category</div>
                </div>
              </div>
            </div>
          )}

          {watchlistScreening ? (
            <WatchlistHitDetail match={match} getConfidenceColor={getConfidenceColor} />
          ) : (
            <ReferenceDatabaseHitDetail
              match={match}
              getRiskBadgeColor={getRiskBadgeColor}
              getConfidenceColor={getConfidenceColor}
            />
          )}

          {match.profileNotes && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Source notes</h3>
              <div className="border rounded-lg p-4 bg-yellow-50 border-yellow-200 text-sm text-gray-700 leading-relaxed">
                {String(sanitizeVendorBranding(match.profileNotes) || '')
                  .split(/(?<=\.)\s+/)
                  .map((line, index) => (
                    <p key={index} className={index > 0 ? 'mt-2' : ''}>
                      {line}
                    </p>
                  ))}
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            <button type="button" onClick={generateReport} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Generate Report
            </button>
            <button type="button" onClick={() => setCaseForm((f) => ({ ...f, open: !f.open, error: '' }))} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
              Create Case
            </button>
            <button type="button" onClick={exportData} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
              Export Data
            </button>
          </div>
          {caseForm.open && (
            <div className="mt-4 p-4 border border-green-200 bg-green-50 rounded-lg">
              <label className="block text-sm font-medium text-gray-800 mb-1">
                Patron member ID to open the investigation against
              </label>
              <div className="flex gap-2">
                <input
                  className="border rounded px-3 py-2 text-sm flex-1 bg-white"
                  value={caseForm.memberId}
                  onChange={(e) => setCaseForm((f) => ({ ...f, memberId: e.target.value }))}
                  placeholder="e.g. 51566"
                />
                <button
                  type="button"
                  disabled={caseForm.saving}
                  onClick={submitCase}
                  className="px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 disabled:opacity-50"
                >
                  {caseForm.saving ? 'Creating…' : 'Create case'}
                </button>
              </div>
              {caseForm.error && <p className="mt-2 text-sm text-red-700">{caseForm.error}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function WatchlistHitDetail({ match, getConfidenceColor }) {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">This hit</h3>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div>
          <dt className="text-gray-600">Match kind</dt>
          <dd className="font-medium text-gray-900">
            {(match.detailKindLabel || match.matchKind || '—').replace(/_/g, ' ')}
          </dd>
        </div>
        <div>
          <dt className="text-gray-600">Confidence</dt>
          <dd className={`font-medium ${getConfidenceColor(match.confidence)}`}>
            {match.confidence}%
          </dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-gray-600">Watchlist / source</dt>
          <dd className="text-gray-900">{match.riskDescription || '—'}</dd>
        </div>
        {match.greenidVerificationId && (
          <div>
            <dt className="text-gray-600">Screening reference</dt>
            <dd className="font-mono text-gray-900">{match.greenidVerificationId}</dd>
          </div>
        )}
      </dl>
      {(match.matchKind === 'pep_group' || match.matchKind === 'watchlist_list') && (
        <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-3">
          This row is a <strong>watchlist group</strong>, not a separate person record. PEP status for
          the screened subject comes from membership in lists like this one, plus structured profile
          matches at screening level.
        </p>
      )}
      {match.matchKind === 'media_article' && (
        <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-3 space-y-2">
          <p>
            Names in the headline may refer to the subject or to third parties. Confirm context before
            escalation.
          </p>
          <AmlMediaSource match={match} />
        </div>
      )}
      {match.matchKind === 'person' && /^ASSOCIATE/i.test(String(match.riskCategory || '')) && (
        <p className="text-sm text-blue-900 bg-blue-50 border border-blue-200 rounded p-3">
          This is a <strong>related person (associate)</strong> returned by screening, not necessarily
          the person you searched for.
        </p>
      )}
    </div>
  )
}

function ReferenceDatabaseHitDetail({ match, getRiskBadgeColor, getConfidenceColor }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Basic information</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between gap-4">
            <span className="font-medium text-gray-700">Risk category</span>
            <span className={`px-2 py-1 text-xs font-medium rounded ${getRiskBadgeColor(match.riskCategory)}`}>
              {match.riskCategory} — {match.riskDescription}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">Match confidence</span>
            <span className={`font-medium ${getConfidenceColor(match.confidence)}`}>
              {match.confidence}%
            </span>
          </div>
          {match.gender && (
            <div className="flex justify-between">
              <span className="font-medium text-gray-700">Gender</span>
              <span className="text-gray-600">{match.gender}</span>
            </div>
          )}
          {match.nationality && (
            <div className="flex justify-between">
              <span className="font-medium text-gray-700">Nationality</span>
              <span className="text-gray-600">{match.nationality}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">Status</span>
            <span className={match.active ? 'text-green-600' : 'text-red-600'}>
              {match.active ? 'Active' : 'Inactive'}
            </span>
          </div>
          {match.lastUpdated && (
            <div className="flex justify-between">
              <span className="font-medium text-gray-700">Last updated</span>
              <span className="text-gray-600">{match.lastUpdated}</span>
            </div>
          )}
        </div>
      </div>
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Profile data</h3>
        {match.countries?.length > 0 && (
          <div className="text-sm">
            <span className="font-medium text-gray-700">Countries: </span>
            {match.countries.join(', ')}
          </div>
        )}
        {match.aliases?.length > 0 && (
          <div className="text-sm">
            <span className="font-medium text-gray-700 block mb-1">Aliases</span>
            {match.aliases.map((a, i) => (
              <div key={i} className="text-gray-600 bg-gray-50 px-2 py-1 rounded mb-1">
                {a}
              </div>
            ))}
          </div>
        )}
        {match.roles?.length > 0 && (
          <div className="text-sm">
            <span className="font-medium text-gray-700 block mb-1">Roles</span>
            {match.roles.map((r, i) => (
              <div key={i} className="text-blue-700 bg-blue-50 px-2 py-1 rounded mb-1">
                {r}
              </div>
            ))}
          </div>
        )}
        {match.sanctions?.length > 0 && (
          <div className="text-sm">
            <span className="font-medium text-red-700 block mb-1">Sanctions</span>
            {match.sanctions.map((s, i) => (
              <div key={i} className="text-red-600 bg-red-50 px-2 py-1 rounded mb-1">
                {s}
              </div>
            ))}
          </div>
        )}
        {!match.aliases?.length && !match.roles?.length && !match.countries?.length && (
          <p className="text-sm text-gray-500">No extended profile fields on this record.</p>
        )}
      </div>
    </div>
  )
}
